import {
  BoundaryEditor,
  DateTimePickerController,
  GroupCodesController,
  SwitchController,
  TextFieldController,
} from '@/components/controls';
import { isIncidentNumber, isOccurrenceNumber } from '@/utils';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { Fragment, useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

export function SceneEditorDialog({
  scene,
  isOpen,
  onClose,
  onDelete,
  groupCodes,
  isDeleting,
}) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: scene,
  });
  const [endTime, startTime] = useWatch({
    control,
    name: ['endTime', 'startTime'],
  });

  useEffect(() => {
    reset(scene);
  }, [reset, scene, isOpen]);

  function handleClose() {
    onClose();
  }

  return (
    <form id="scene-editor-form" onSubmit={handleSubmit(onClose)}>
      <Dialog
        open={isOpen && !!scene}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{`${scene.code ? 'Edit' : 'Add'} Scene`}</DialogTitle>
        <DialogContent>
          <DialogContentText gutterBottom>
            Please fill in the form below to add a scene. If groups are not
            specified then the scene will be visible to all.
          </DialogContentText>
          <Stack spacing={1} direction="row">
            <Stack spacing={1} sx={{ width: 240 }}>
              <TextFieldController
                name="name"
                label="Name"
                control={control}
                rules={{ required: 'Required' }}
              />
              <TextFieldController
                name="attributes.incidentCode"
                label="Incident Number"
                control={control}
                rules={{
                  validate: async (value) =>
                    !value || (await isIncidentNumber(value)),
                }}
              />
              <TextFieldController
                name="attributes.occurrenceNumber"
                label="Occurrence Number"
                control={control}
                rules={{
                  validate: async (value) =>
                    !value || (await isOccurrenceNumber(value)),
                }}
              />
              <DateTimePickerController
                name="startTime"
                label="Start Time"
                control={control}
                rules={{ required: 'Required' }}
                minDate={new Date('1900-01-01')}
                maxDate={endTime ?? new Date('2100-01-01')}
              />
              <DateTimePickerController
                name="endTime"
                label="End Time"
                control={control}
                rules={{ required: 'Required' }}
                minDate={startTime ?? new Date('1900-01-01')}
                maxDate={new Date('2100-01-01')}
              />
              <GroupCodesController
                label="Groups"
                control={control}
                name="groupCodes"
                ancestors={groupCodes}
              />
              <TextFieldController
                name="attributes.requiredAttendance"
                label="Required Attendance"
                defaultValue={0}
                control={control}
                rules={{ required: 'Required' }}
                parse={(value) => (value == null || value === '' ? '' : value)}
                format={(value) => (value === '' ? '' : +value)}
              />
              <Box sx={{ flexGrow: 1 }} />
              <SwitchController
                name="attributes.showOnLiveMap"
                label="Show on Live Map"
                control={control}
              />
            </Stack>
            <Controller
              name="boundary"
              control={control}
              rules={{ required: 'Required' }}
              render={({
                field: { value, disabled, onChange },
                fieldState: { error, isDirty },
              }) => (
                <BoundaryEditor
                  value={value}
                  onChange={onChange}
                  disabled={disabled}
                  error={error?.message}
                  isDirty={isDirty}
                  sx={{ width: '100%', maxHeight: 1000, minHeight: 640 }}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {!!scene.code && (
            <Fragment>
              <Button color="error" onClick={onDelete} disabled={isDeleting}>
                Delete
              </Button>
              <Box sx={{ flexGrow: 1 }} />
            </Fragment>
          )}
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            form="scene-editor-form"
            type="submit"
            disabled={isSubmitting}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
}
